import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from './Header';
import Footer from './Footer';
import { useJobs, useCategories, useLocations } from '../hooks/useApi';

const LocationJobs = () => {
  const { locationName } = useParams();
  const navigate = useNavigate();
  const { data: jobsData } = useJobs();
  const { data: categoriesData } = useCategories();
  const { data: locationsData } = useLocations();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [salaryRange, setSalaryRange] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  console.log('LocationJobs component props:', {
    locationName,
    jobsData: jobsData?.length,
    categoriesData,
    locationsData
  });


    useEffect(() => {
      window.scrollTo(0, 0); // Scroll to top when this component mounts
    }, []);

  // Handle navigation when locationName is undefined
  useEffect(() => {
    if (!locationName) {
      console.log('No locationName provided in URL, redirecting to browse-jobs');
      navigate('/browse-jobs');
    }
  }, [locationName, navigate]);

  // Function to get main city from full location name
  const getMainCity = (location) => {
    if (!location) {
      console.log('Empty location provided to getMainCity');
      return '';
    }
    const parts = location.split(',');
    const mainCity = parts[parts.length - 1].trim();
    console.log('Extracting main city from location:', location, 'Result:', mainCity);
    return mainCity;
  };

  // Get the main city name from the URL parameter
  const mainCity = useMemo(() => {
    if (!locationName || !locationsData) {
      console.log('Missing required data:', { locationName, locationsData });
      return '';
    }
    
    console.log('Finding main city for locationName:', locationName);
    
    // First try to find a match in the full location names
    const fullLocation = Object.values(locationsData).find(location => {
      const normalizedLocation = location.toLowerCase().replace(/\s+/g, '-');
      const normalizedParam = locationName.toLowerCase();
      console.log('Comparing:', normalizedLocation, 'with', normalizedParam);
      return normalizedLocation === normalizedParam;
    });
    
    console.log('Full location match:', fullLocation);
    
    if (fullLocation) {
      const city = getMainCity(fullLocation);
      console.log('Found city from full location:', city);
      return city;
    }
    
    // If no full match, try to find a match in the main cities
    const mainCities = new Set();
    Object.values(locationsData).forEach(location => {
      const city = getMainCity(location);
      if (city) {
        mainCities.add(city);
      }
    });
    
    const city = Array.from(mainCities).find(city => {
      const normalizedCity = city.toLowerCase().replace(/\s+/g, '-');
      const normalizedParam = locationName.toLowerCase();
      console.log('Comparing main city:', normalizedCity, 'with', normalizedParam);
      return normalizedCity === normalizedParam;
    });
    
    console.log('Found city from main cities:', city);
    return city || '';
  }, [locationsData, locationName]);

  // Filter jobs based on location and other criteria
  const filteredJobs = useMemo(() => {
    if (!jobsData || !mainCity) {
      console.log('No jobs or main city:', { jobsData: jobsData?.length, mainCity });
      return [];
    }
    
    console.log('Filtering jobs for main city:', mainCity);
    const result = jobsData.filter(job => {
      const jobLocation = job.location_name || job.location || '';
      const jobMainCity = getMainCity(jobLocation);
      const matchesLocation = typeof jobMainCity === 'string' && 
                            jobMainCity.toLowerCase() === mainCity.toLowerCase();
      
      console.log('Job location check:', {
        jobId: job.job_id,
        jobLocation,
        jobMainCity,
        mainCity,
        matchesLocation
      });

      const matchesSearch = !searchQuery || 
                          (job.title && job.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
                          (job.company_name && job.company_name.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategories.length === 0 || 
                            selectedCategories.includes(job.category_name);
      const matchesSalary = !salaryRange || 
                          (job.salary && job.salary >= parseInt(salaryRange));
      
      return matchesLocation && matchesSearch && matchesCategory && matchesSalary;
    });

    console.log('Filtered jobs result:', {
      totalJobs: jobsData.length,
      filteredCount: result.length,
      mainCity
    });

    return result;
  }, [jobsData, mainCity, searchQuery, selectedCategories, salaryRange]);

  // Sort jobs
  const sortedJobs = useMemo(() => {
    if (!filteredJobs) return [];
    
    return [...filteredJobs].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'salary-high':
          return (b.salary || 0) - (a.salary || 0);
        case 'salary-low':
          return (a.salary || 0) - (b.salary || 0);
        default:
          return 0;
      }
    });
  }, [filteredJobs, sortBy]);

  // Get unique categories from filtered jobs
  const uniqueCategories = useMemo(() => {
    if (!filteredJobs) return [];
    const categories = new Set();
    filteredJobs.forEach(job => {
      if (job.category_name) {
        categories.add(job.category_name);
      }
    });
    return Array.from(categories).sort();
  }, [filteredJobs]);

  // Get top companies in this location
  const topCompanies = useMemo(() => {
    if (!filteredJobs) return [];
    const companyCounts = {};
    
    filteredJobs.forEach(job => {
      const company = job.company_name || 'Other';
      companyCounts[company] = (companyCounts[company] || 0) + 1;
    });

    return Object.entries(companyCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [filteredJobs]);

  // Add handleJobClick function
  const handleJobClick = (jobId, jobTitle, jobCode) => {
    const jobSlug = `${jobTitle.toLowerCase().replace(/\s+/g, '-')}-${jobCode}`;
    navigate(`/jobs/${jobCode}`)
  };

  if (!mainCity) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <div className="error-page">
              <h2>404</h2>
              <h3>Location Not Found</h3>
              <p>The location you are looking for does not exist.</p>
              <Link to="/browse-jobs" className="btn btn-primary">
                Browse All Jobs
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Jobs in ${mainCity} | Fresher Job Adda`}</title>
        <meta name="description" content={`Find the latest jobs in ${mainCity}. Browse through various job opportunities and apply for your dream job.`} />
      </Helmet>
      
      <Header />
      
      {/* Page Title Section */}
      <div className="page-title">
        <div className="container">
          <div className="page-caption">
            <h2>{`Jobs in ${mainCity}`}</h2>
            <p>
              <Link to="/">Home</Link> <i className="fa fa-angle-double-right"></i> 
              <Link to="/browse-jobs">Jobs</Link> <i className="fa fa-angle-double-right"></i>
              {mainCity}
            </p>
          </div>
        </div>
      </div>

      {/* Jobs Section */}
      <section className="padd-top-50 padd-bot-80" style={{backgroundColor: "#f1f5f9"}}>
        <div className="container">
          <div className="row mb-4">
            <div className="col-12">
              <h2 className="mb-0">Open Positions</h2>
              <p className="text-muted">
                {sortedJobs.length} {sortedJobs.length === 1 ? 'job' : 'jobs'} available in {mainCity}
              </p>
            </div>
          </div>
          
          <div className="row">
            {sortedJobs.length === 0 ? (
              <div className="col-12 text-center py-5">
                <div className="alert alert-info">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  No jobs available in this location at the moment
                </div>
              </div>
            ) : (
              sortedJobs.map(job => (
                <div className="col-md-3 col-sm-6" key={job.id}>
                  <div
                    className="utf_grid_job_widget_area pt-0"
                    onClick={() => handleJobClick(job.id, job.title, job.job_code)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="u-content px-0" style={{ position: "relative" }}>
                      <div className="avatar-square box-80" style={{ width: "100%", position: "relative" }}>
                        <a onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleJobClick(job.id, job.title, job.job_code);
                        }}>
                          <div 
                            className="category-icon-fallback"
                            style={{
                              display: 'flex',
                              width: "100%",
                              height: "200px",
                              backgroundColor: "#f8f9fa",
                              justifyContent: "center",
                              alignItems: "center",
                              fontSize: "4rem",
                              color: "#26ae61",
                              borderRadius: "8px",
                              padding: "2rem",
                              position: "relative",
                              zIndex: 1
                            }}
                          >
                            {/* Fallback icon - always present but hidden by default */}
                            <i
                              className="fa-solid fa-house"
                              style={{
                                fontSize: "3.5rem",
                                color: "#ccc",
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                                display: "none" // Initially hidden
                              }}
                            ></i>

                            {/* Company image */}
                            {job.company_image_url?.trim() ? (
                              <img
                                src={job.company_image_url}
                                alt={job.company_name}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  const fallbackIcon = e.target.previousSibling;
                                  if (fallbackIcon) {
                                    fallbackIcon.style.display = "block"; // Show icon if image fails
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  height: "200px",
                                  objectFit: "cover",
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  borderRadius: "8px",
                                  zIndex: 2
                                }}
                                className="company-logo"
                              />
                            ) : (
                              <i
                                className="fa-solid fa-house"
                                style={{
                                  fontSize: "3.5rem",
                                  color: "#ccc",
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 1,
                                  display: "block" // Show icon if no image URL
                                }}
                              ></i>
                            )}
                          </div>
                        </a>
                      </div>
                      
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 3 }}>
                        <span className="job-type bg-success border-1 text-white position-absolute top-2 start-2">
                          {job.category_name}
                        </span>
                      </div>
                      
                      <h5 className="pt-3">
                        <a onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleJobClick(job.id, job.title, job.job_code);
                        }}>
                          {job.title}
                        </a>
                      </h5>
                      
                      <p className="text-muted mb-0">
                        <i className="fa fa-briefcase"></i> {job.position || 'Full Time'}
                      </p>
                      <p className="text-muted mb-0">
                        <i className="fa fa-industry text-capitalize"></i> {job.company_name}
                      </p>
                      <p className="text-muted py-0 my-0">
                        <i className="fas fa-wallet"></i> {job.salary || '0'}+ LPA
                      </p>
                      <p className="text-muted py-0 my-0 text-capitalize">
                        <i className="fas fa-map-marker-alt"></i> {job.location_name}
                      </p>
                      <p className="text-muted py-0 my-0 text-capitalize">
                        <i className="fas fa-code"></i> {job.job_code}
                      </p>
                    </div>
                    
                    <div className="utf_apply_job_btn_item">
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleJobClick(job.id, job.title, job.job_code);
                        }}
                        className="btn job-browse-btn btn-radius br-light"
                      >
                        Apply
                      </a>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
      
      <Footer />
    </>
  );
};

export default LocationJobs; 